import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { Chart } from '@/components/ui/chart';
import { DISTANCE_UNITS } from '@/lib/constants';
import { Co2Emission, EmissionsChartData } from '@/lib/models/emissions/types';
import {
  getEmissionsRadialChartConfig,
  getEmissionsRadialChartData,
} from '@/lib/models/emissions/utils/transform-chart-data';
import { RadialChartData } from '@/types';
import { formatDistance } from '@/utils/format';
import { formatEmissionsString } from '@/utils/format/emissions';
import { tv } from '@/utils/styles';

export interface EmissionsTableProps {
  title: string;
  description: string;
  chartData: EmissionsChartData[] | RadialChartData[];
  totalCo2e: Co2Emission;
}

const EmissionsTable = ({ title, description, chartData, totalCo2e }: EmissionsTableProps) => {
  const { chartMarker, sectionTitle, subText, tableWrapper, th, td } = styles();
  const hasAdditionalData = 'data' in chartData[0] && chartData[0].data;

  const chartConfig = getEmissionsRadialChartConfig(chartData);
  const chartDataWithFill = getEmissionsRadialChartData(chartData);

  return (
    <div className="mt-8">
      <div className="flex items-center gap-4">
        <Chart
          data={chartDataWithFill}
          chartConfig={chartConfig}
          type="pie"
          settings={{
            pie: {
              dataKey: 'percent',
              nameKey: 'name',
              innerRadius: 16,
              outerRadius: 24,
              isAnimationActive: false,
            },
            tooltip: {
              hide: true,
            },
          }}
          className="size-12" // Match the outerRadius x 2
        />
        <div>
          <Heading className={sectionTitle()} variant="h4">
            {title}
          </Heading>
          <Text className={subText()}>{description}</Text>
        </div>
      </div>
      <div className={tableWrapper()}>
        <table className="min-w-full divide-y divide-grey-200">
          <thead className="bg-grey-50">
            <tr>
              <th className={th()}>Mode</th>
              <th className={th()}>Percentage</th>
              <th className={th()}>Emissions</th>
              {hasAdditionalData && <th className={th()}>Distance</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-200">
            {chartData.map((row) => {
              const { color, value, name } = row;
              const rowData = 'data' in row && row.data ? row.data : undefined;

              return (
                <tr key={name}>
                  <td className={td()}>
                    <div className="flex items-center gap-2">
                      <span className={chartMarker()} style={{ background: color }} />
                      {name}
                    </div>
                  </td>
                  <td className={td()}>
                    <div className="grid grid-cols-2 items-center gap-3">{row.percent}%</div>
                  </td>
                  <td className={td()}>{formatEmissionsString(value, totalCo2e?.unit)}</td>
                  {hasAdditionalData && (
                    <td className={td()}>{formatDistance(rowData?.distance || 0, DISTANCE_UNITS.KM)}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    chartMarker: 'block h-2 w-4 rounded-sm bg-grey-200',
    sectionTitle: 'mb-1 text-base font-medium leading-snug',
    subText: 'm-0 text-sm text-grey-600 [&_b]:font-semibold [&_b]:text-text-primary',
    tableWrapper: 'mt-4 overflow-hidden rounded-lg border border-grey-200',
    th: 'px-6 py-3 text-start text-xs font-medium uppercase text-grey-500',
    td: 'whitespace-nowrap px-6 py-3 text-baseSm text-grey-800',
  },
});

export { EmissionsTable };
